import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { TempToken, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

const INACTIVITY_MS = 15 * 60 * 1000; // 15 min

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [twoFA, setTwoFA] = useState(null); // { setup_required, email, role }

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    TempToken.clear();
    setUser(false);
    setTwoFA(null);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (_) {
      setUser(false);
      return null;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.two_factor_required) {
        TempToken.set(data.temp_token);
        setTwoFA({ setup_required: data.setup_required, email: data.email, role: data.role });
        return { twoFA: true, setup: data.setup_required };
      }
      setUser(data.user);
      return { user: data.user };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      throw new Error(msg);
    }
  }, []);

  const verify2FA = useCallback(async (code) => {
    try {
      const { data } = await api.post("/auth/2fa/login", { code });
      TempToken.clear();
      setUser(data.user);
      setTwoFA(null);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      throw new Error(msg);
    }
  }, []);

  const setup2FA = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      return data;
    } catch (e) {
      throw new Error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  // Boot check
  useEffect(() => {
    // If returning from Emergent Google OAuth callback, defer to AuthCallback
    // which will exchange session_id and establish the JWT cookie first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    fetchMe();
  }, [fetchMe]);

  // Auto-logout on inactivity
  useEffect(() => {
    if (!user) return;
    let t;
    const reset = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        toast.info("Sesi berakhir karena tidak aktif.");
        logout();
      }, INACTIVITY_MS);
    };
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(t);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const value = useMemo(
    () => ({ user, twoFA, login, verify2FA, setup2FA, logout, refresh: fetchMe }),
    [user, twoFA, login, verify2FA, setup2FA, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
